import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { AppProvider } from '@toolpad/core/AppProvider';
import { ServiceDashboardWrapper } from './ServiceDashboard.styled';
import DashboardNavigationBar from '../DashboardNavigationBar/DashboardNavigationBar';
import StorefrontOfflineAlert from '../StorefrontOfflineAlert/StorefrontOfflineAlert';
import {
  DashboardContainer,
  SidebarContainer,
  ContentWrapper,
} from '../BusinessDashboard/BusinessDashboard.styled';

// ─── API Services ───────────────────────────────────────────────────────────
import { accountService } from '../../services/api/accountService';
import { businessService } from '../../services/api/businessService';
import { servicesService } from '../../services/api/servicesService';
import { appTheme } from '../../theme/appTheme';
import {
  ALL_TRADES,
  getTradeSubservicesFlat,
  isBusinessDefinedTrade,
} from '../../constants/mockData/allTrades';
import BusinessDefinedServiceModal from '../common/BusinessDefinedServiceModal/BusinessDefinedServiceModal';
import StorefrontServiceCardPreview from '../common/StorefrontServiceCardPreview/StorefrontServiceCardPreview';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import { resolveSubserviceTitle } from '../../utils/subserviceDisplay';
import { formatCurrency, formatCurrencyRange } from '../../utils/formatPrice';

// ─── Icons ──────────────────────────────────────────────────────────────────
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AlertCircleIcon from '@mui/icons-material/ErrorOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShieldIcon from '@mui/icons-material/Shield';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';

// ─── Pricing options for the modal ──────────────────────────────────────────
// NOTE: 'Dynamic' was removed — the new API only supports Fixed | Range | NA.
const PRICE_TYPE_OPTIONS = [
  { id: 'Range', label: 'Price range', hint: 'e.g. $85 – $220' },
  { id: 'Fixed', label: 'Fixed price', hint: 'e.g. $150 flat' },
  { id: 'NA', label: 'Not shown', hint: 'Hide pricing from customers' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// API ↔ LOCAL STATE MAPPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Build local enrollment state from the Services[] array on the business response.
 *
 * Local shape:
 *   trades: {
 *     [serviceType]: {
 *       ServiceId: string,
 *       Quoting: {...},
 *       IsSchedulable: boolean,
 *       subServices: {
 *         [subServiceType]: { SubServiceType, Tagline, Pricing }
 *       }
 *     }
 *   }
 *
 * Note: a Service with zero SubServices is still recorded here — the user
 * has enrolled in the trade but hasn't activated any sub-services yet. The
 * UI shows it as "Browsing" until at least one sub-service is added.
 */
function buildTradesFromApiServices(services) {
  const trades = {};

  (services || []).forEach((service) => {
    const tradeId = service.ServiceType;
    const subServices = {};

    (service.SubServices || []).forEach((sub) => {
      subServices[sub.SubServiceType] = {
        SubServiceType: sub.SubServiceType,
        Title: sub.Title || '',
        Tagline: sub.Tagline || '',
        Description: sub.Description || '',
        Pricing: sub.Pricing || { PriceType: 'NA' },
        IsCustom: sub.IsCustom === true,
      };
    });

    trades[tradeId] = {
      ServiceId: service.ServiceId,
      Quoting: service.Quoting || { FreeQuotes: true, QuotingMethod: ['phone'] },
      IsSchedulable: service.IsSchedulable || false,
      FinancingAvailable: service.FinancingAvailable || false,
      subServices,
    };
  });

  return trades;
}

/**
 * Rebuild a trade's local state from the full ServiceResponse returned by
 * PATCH /service-api/{ServiceId}. Pure — used after any service-level or
 * sub-service PATCH so the two call sites can't drift.
 */
function rebuildTradeFromPatchResponse(prevTrade, updated) {
  const updatedSubs = {};
  (updated.SubServices || []).forEach((sub) => {
    updatedSubs[sub.SubServiceType] = {
      SubServiceType: sub.SubServiceType,
      Title: sub.Title || '',
      Tagline: sub.Tagline || '',
      Description: sub.Description || '',
      Pricing: sub.Pricing || { PriceType: 'NA' },
      IsCustom: sub.IsCustom === true,
    };
  });

  return {
    ServiceId: updated.ServiceId,
    Quoting: updated.Quoting || prevTrade.Quoting,
    IsSchedulable:
      updated.IsSchedulable != null ? updated.IsSchedulable : prevTrade.IsSchedulable,
    FinancingAvailable:
      updated.FinancingAvailable != null
        ? updated.FinancingAvailable
        : prevTrade.FinancingAvailable,
    subServices: updatedSubs,
  };
}

/**
 * Build the Pricing object from the modal's local state.
 * The required fields depend on PriceType:
 *   - Fixed → Price
 *   - Range → at least one of Min / Max
 *   - NA    → no extra fields
 */
function buildPricingFromModal(modalConfig) {
  const pricing = { PriceType: modalConfig.priceType };

  if (modalConfig.priceType === 'Fixed') {
    pricing.Price = Number(modalConfig.fixedPrice);
  } else if (modalConfig.priceType === 'Range') {
    if (modalConfig.priceMin !== '' && modalConfig.priceMin != null) {
      pricing.Min = Number(modalConfig.priceMin);
    }
    if (modalConfig.priceMax !== '' && modalConfig.priceMax != null) {
      pricing.Max = Number(modalConfig.priceMax);
    }
  }

  return pricing;
}

/**
 * Build the SubService payload sent on PATCH /service-api/{ServiceId}.
 * The API does FULL OBJECT REPLACEMENT on sub-services — every field that
 * should be preserved must be included.
 */
function buildSubServicePayload(subServiceType, modalConfig) {
  return {
    SubService: {
      SubServiceType: subServiceType,
      Tagline: modalConfig.tagline || '',
      Description: (modalConfig.description || '').trim(),
      Pricing: buildPricingFromModal(modalConfig),
    },
  };
}

/**
 * Convert an API SubService object into the modal's local state shape.
 */
function apiSubServiceToModalState(subService) {
  if (!subService) {
    return {
      tagline: '',
      description: '',
      priceType: 'NA',
      priceMin: '',
      priceMax: '',
      fixedPrice: '',
    };
  }

  const pricing = subService.Pricing || {};
  return {
    tagline: subService.Tagline || '',
    description: subService.Description || '',
    priceType: pricing.PriceType || 'NA',
    priceMin: pricing.Min != null ? String(pricing.Min) : '',
    priceMax: pricing.Max != null ? String(pricing.Max) : '',
    fixedPrice: pricing.Price != null ? String(pricing.Price) : '',
  };
}

// ─── Modal validation ───────────────────────────────────────────────────────

const validateModalConfig = (config) => {
  const errors = {};

  if (config.priceType === 'Range') {
    if (!config.priceMin && !config.priceMax) {
      errors.pricing = 'Set a minimum or maximum price';
    } else if (
      config.priceMin &&
      config.priceMax &&
      Number(config.priceMin) > Number(config.priceMax)
    ) {
      errors.pricing = 'Minimum price cannot exceed maximum';
    }
  }

  if (config.priceType === 'Fixed' && !config.fixedPrice) {
    errors.pricing = 'Enter a fixed price';
  }

  return errors;
};

/** Default empty state for ServiceConfigModal (stable reference for hooks). */
const EMPTY_MODAL_CONFIG = {
  tagline: '',
  description: '',
  priceType: 'NA',
  priceMin: '',
  priceMax: '',
  fixedPrice: '',
};

// ═══════════════════════════════════════════════════════════════════════════════
// TRADE SETTINGS MODAL — collects Quoting + FinancingAvailable config.
// Used both to enroll in a new trade (POST /service-api/) and to edit the
// service-level config of a trade the business is already enrolled in
// (PATCH /service-api/{ServiceId}).
// ═══════════════════════════════════════════════════════════════════════════════

const TradeSettingsModal = ({
  open,
  onClose,
  onConfirm,
  trade,
  saving,
  mode = 'enroll',
  initialConfig,
}) => {
  const isEdit = mode === 'edit';
  const [freeQuotes, setFreeQuotes] = useState(true);
  const [methods, setMethods] = useState({ 'in-person': false, phone: true });
  const [financingAvailable, setFinancingAvailable] = useState(false);

  useEffect(() => {
    if (open) {
      if (isEdit && initialConfig) {
        setFreeQuotes(initialConfig.FreeQuotes);
        setMethods({
          'in-person': initialConfig.QuotingMethod.includes('in-person'),
          phone: initialConfig.QuotingMethod.includes('phone'),
        });
        setFinancingAvailable(!!initialConfig.FinancingAvailable);
      } else {
        setFreeQuotes(true);
        setMethods({ 'in-person': false, phone: true });
        setFinancingAvailable(false);
      }
    }
  }, [open, trade?.id, isEdit, initialConfig]);

  // Enforce: if FreeQuotes = false → phone is disabled and in-person is auto-selected.
  useEffect(() => {
    if (!freeQuotes) {
      setMethods({ 'in-person': true, phone: false });
    }
  }, [freeQuotes]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const toggleMethod = (key) => {
    // Phone is locked off when free quotes is false
    if (!freeQuotes && key === 'phone') return;
    setMethods((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedMethods = Object.entries(methods)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const valid = selectedMethods.length > 0;

  const handleConfirm = () => {
    if (!valid) return;
    onConfirm(trade.id, {
      FreeQuotes: freeQuotes,
      QuotingMethod: selectedMethods,
      FinancingAvailable: financingAvailable,
    });
  };

  if (!open || !trade) return null;
  const TradeIcon = trade.icon;

  return (
    <div className="config-modal-overlay" onClick={handleOverlayClick}>
      <div className="config-modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="config-modal-header">
          <div className={`config-modal-icon trade-color-${trade.id}`}>
            <TradeIcon sx={{ fontSize: 22 }} />
          </div>
          <div className="config-modal-title-area">
            <Typography variant="subtitle1" className="config-modal-title">
              {isEdit ? `Edit ${trade.name} settings` : `Enroll in ${trade.name}`}
            </Typography>
            <div className="config-modal-subtitle">
              How do you handle quotes for this trade?
            </div>
          </div>
          <button
            className="config-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        {/* Body */}
        <div className="config-modal-body">
          <div className="modal-config-section">
            <div className="modal-config-label">
              <RequestQuoteIcon sx={{ fontSize: 16 }} />
              Free quotes
            </div>
            <div className="modal-config-hint">
              Do you offer quotes at no charge to the customer?
            </div>

            <div className="modal-price-type-options">
              <button
                type="button"
                className={`modal-price-type-pill${freeQuotes ? ' active' : ''}`}
                onClick={() => setFreeQuotes(true)}
              >
                <span className="pill-label">Yes, free quotes</span>
                <span className="pill-hint">Quotes are complimentary</span>
              </button>
              <button
                type="button"
                className={`modal-price-type-pill${!freeQuotes ? ' active' : ''}`}
                onClick={() => setFreeQuotes(false)}
              >
                <span className="pill-label">No, paid quotes</span>
                <span className="pill-hint">Customer pays for a quote</span>
              </button>
            </div>
          </div>

          {/* Quoting method */}
          <div className="modal-config-section">
            <div className="modal-config-label">
              <PersonPinCircleIcon sx={{ fontSize: 16 }} />
              Quoting method
            </div>
            <div className="modal-config-hint">
              {freeQuotes
                ? 'How can customers receive a quote? Select all that apply.'
                : 'Paid quotes are in-person only.'}
            </div>

            <div className="modal-price-type-options">
              <button
                type="button"
                className={`modal-price-type-pill${methods['in-person'] ? ' active' : ''}`}
                onClick={() => toggleMethod('in-person')}
              >
                <span className="pill-label">
                  <PersonPinCircleIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: '-2px' }} />
                  In-person
                </span>
                <span className="pill-hint">On-site visit to provide a quote</span>
              </button>
              <button
                type="button"
                disabled={!freeQuotes}
                className={`modal-price-type-pill${methods.phone ? ' active' : ''}${!freeQuotes ? ' disabled' : ''}`}
                onClick={() => toggleMethod('phone')}
                title={!freeQuotes ? 'Phone quotes are not available for paid quotes' : ''}
              >
                <span className="pill-label">
                  <PhoneIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: '-2px' }} />
                  Phone
                </span>
                <span className="pill-hint">
                  {freeQuotes ? 'Quote provided over the phone' : 'Not available'}
                </span>
              </button>
            </div>

            {!valid && (
              <div className="modal-field-error">
                <AlertCircleIcon sx={{ fontSize: 13 }} />
                Select at least one quoting method
              </div>
            )}
          </div>

          {/* Financing */}
          <div className="modal-config-section">
            <div className="modal-config-label">
              <AttachMoneyIcon sx={{ fontSize: 16 }} />
              Financing
            </div>
            <div className="modal-config-hint">
              Do you offer financing options for this trade?
            </div>

            <div className="modal-price-type-options">
              <button
                type="button"
                className={`modal-price-type-pill${financingAvailable ? ' active' : ''}`}
                onClick={() => setFinancingAvailable(true)}
              >
                <span className="pill-label">Yes, financing available</span>
                <span className="pill-hint">Shown to customers on your storefront</span>
              </button>
              <button
                type="button"
                className={`modal-price-type-pill${!financingAvailable ? ' active' : ''}`}
                onClick={() => setFinancingAvailable(false)}
              >
                <span className="pill-label">No financing</span>
                <span className="pill-hint">Customers pay directly</span>
              </button>
            </div>
          </div>

          {/* Info */}
          {!isEdit && (
            <div className="info-banner" style={{ marginBottom: 0 }}>
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              <span>
                After enrolling, add at least one service so customers can find you.
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="config-modal-footer">
          <div className="config-modal-footer-left">
            <ShieldIcon sx={{ fontSize: 14 }} />
            <span>You can change this later</span>
          </div>

          <div className="config-modal-footer-actions">
            <button className="modal-btn-secondary" onClick={onClose} type="button">
              Cancel
            </button>
            <button
              className={`modal-btn-primary${saving ? ' saving' : ''}`}
              onClick={handleConfirm}
              disabled={!valid || saving}
              type="button"
            >
              {saving ? (
                <>
                  <NkySpinner size="sm" onDark />
                  {isEdit ? 'Saving…' : 'Enrolling…'}
                </>
              ) : (
                <>
                  <CheckIcon sx={{ fontSize: 16 }} />
                  {isEdit ? 'Save Changes' : 'Enroll'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SERVICE CONFIG MODAL COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const ServiceConfigModal = ({
  open,
  onClose,
  onSave,
  subservice,
  tradeId,
  existingSubService, // API SubService object or null
  saving: externalSaving,
}) => {
  const [config, setConfig] = useState(EMPTY_MODAL_CONFIG);
  const [currentStep, setCurrentStep] = useState(1);
  const [touched, setTouched] = useState({});

  // Reset state when modal opens with new subservice
  useEffect(() => {
    if (open) {
      if (existingSubService) {
        setConfig(apiSubServiceToModalState(existingSubService));
      } else {
        setConfig({ ...EMPTY_MODAL_CONFIG });
      }
      setCurrentStep(1);
      setTouched({});
    }
  }, [open, subservice?.id, existingSubService]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const currentErrors = validateModalConfig(config);
  const allValid = Object.keys(currentErrors).length === 0;

  const stepValid =
    (currentStep === 1 && !currentErrors.pricing) || currentStep === 2;

  const handleNext = () => {
    const validationErrors = validateModalConfig(config);
    if (currentStep === 1) {
      setTouched((p) => ({ ...p, pricing: true }));
      if (!validationErrors.pricing) setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSave = () => {
    const validationErrors = validateModalConfig(config);
    setTouched({ pricing: true, tagline: true });

    if (Object.keys(validationErrors).length > 0) {
      if (validationErrors.pricing) {
        setCurrentStep(1);
      }
      return;
    }

    onSave(tradeId, subservice?.id, config);
  };

  const steps = [
    { num: 1, label: 'Pricing', isComplete: !currentErrors.pricing },
    { num: 2, label: 'Details', isComplete: true },
  ];

  if (!open || !subservice) return null;

  return (
    <div className="config-modal-overlay" onClick={handleOverlayClick}>
      <div className="config-modal" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="config-modal-header">
          <div className={`config-modal-icon trade-color-${tradeId}`}>
            <SettingsIcon sx={{ fontSize: 22 }} />
          </div>
          <div className="config-modal-title-area">
            <Typography variant="subtitle1" className="config-modal-title">
              {existingSubService ? 'Edit' : 'Configure'} {subservice?.label}
            </Typography>
            <div className="config-modal-subtitle">
              Set up pricing and details for this service.
            </div>
          </div>
          <button
            className="config-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="config-modal-steps">
          {steps.map((step, i) => (
            <React.Fragment key={step.num}>
              <div className="config-step-item">
                <div
                  className={`config-step-number ${
                    currentStep === step.num
                      ? 'active'
                      : step.isComplete
                        ? 'complete'
                        : 'pending'
                  }`}
                >
                  {step.isComplete && currentStep !== step.num ? (
                    <CheckIcon sx={{ fontSize: 13, strokeWidth: 3 }} />
                  ) : (
                    step.num
                  )}
                </div>
                <span
                  className={`config-step-label ${
                    currentStep === step.num
                      ? 'active'
                      : step.isComplete
                        ? 'complete'
                        : ''
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`config-step-connector ${
                    steps[i].isComplete ? 'complete' : ''
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="config-modal-body">
          {/* Step 1: Pricing */}
          {currentStep === 1 && (
            <div className="modal-config-section">
              <div className="modal-config-label">
                <AttachMoneyIcon sx={{ fontSize: 16 }} />
                Pricing
              </div>
              <div className="modal-config-hint">
                Choose how your pricing is displayed to customers.
              </div>

              <div className="modal-price-type-options">
                {PRICE_TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`modal-price-type-pill${config.priceType === opt.id ? ' active' : ''}`}
                    onClick={() => updateField('priceType', opt.id)}
                  >
                    <span className="pill-label">{opt.label}</span>
                    <span className="pill-hint">{opt.hint}</span>
                  </button>
                ))}
              </div>

              {config.priceType === 'Range' && (
                <div className="modal-price-row">
                  <div className="modal-price-group">
                    <div className="modal-price-label">Minimum</div>
                    <div
                      className={`modal-price-input-wrap${
                        touched.pricing && currentErrors.pricing ? ' error' : ''
                      }`}
                    >
                      <div className="modal-price-prefix">$</div>
                      <input
                        type="number"
                        placeholder="0"
                        value={config.priceMin}
                        onChange={(e) => updateField('priceMin', e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>

                  <div className="modal-price-or">to</div>

                  <div className="modal-price-group">
                    <div className="modal-price-label">Maximum</div>
                    <div
                      className={`modal-price-input-wrap${
                        touched.pricing && currentErrors.pricing ? ' error' : ''
                      }`}
                    >
                      <div className="modal-price-prefix">$</div>
                      <input
                        type="number"
                        placeholder="0"
                        value={config.priceMax}
                        onChange={(e) => updateField('priceMax', e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              )}

              {config.priceType === 'Fixed' && (
                <div className="modal-price-row">
                  <div className="modal-price-group">
                    <div className="modal-price-label">Fixed Price</div>
                    <div
                      className={`modal-price-input-wrap${
                        touched.pricing && currentErrors.pricing ? ' error' : ''
                      }`}
                    >
                      <div className="modal-price-prefix">$</div>
                      <input
                        type="number"
                        placeholder="0"
                        value={config.fixedPrice}
                        onChange={(e) => updateField('fixedPrice', e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              )}

              {touched.pricing && currentErrors.pricing && (
                <div className="modal-field-error">
                  <AlertCircleIcon sx={{ fontSize: 13 }} />
                  {currentErrors.pricing}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Tagline & description */}
          {currentStep === 2 && (
            <div className="modal-config-section">
              <div className="modal-config-label">
                <StorefrontIcon sx={{ fontSize: 16 }} />
                Service Details
              </div>
              <div className="modal-config-hint">
                Add a tagline and description to personalize how this service appears on your
                storefront.
              </div>

              <div className="modal-tagline-field">
                <label className="modal-tagline-label" htmlFor="tagline-input">
                  Tagline (optional)
                </label>
                <input
                  id="tagline-input"
                  type="text"
                  className="modal-tagline-input"
                  placeholder="e.g. Specializes in drain cleaning & clog removal"
                  value={config.tagline}
                  onChange={(e) => updateField('tagline', e.target.value)}
                  maxLength={100}
                />
                <span className="modal-tagline-hint">
                  Shown on your provider card with a checkmark icon.{' '}
                  {config.tagline.length}/100
                </span>
              </div>

              <div className="modal-tagline-field">
                <label className="modal-tagline-label" htmlFor="description-input">
                  Description (optional)
                </label>
                <textarea
                  id="description-input"
                  className="modal-description-input"
                  rows={4}
                  placeholder="Describe what you offer and what customers can expect."
                  value={config.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  maxLength={500}
                />
                <span className="modal-tagline-hint">
                  Shown under your tagline on your storefront card.{' '}
                  {config.description.length}/500
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="config-modal-footer">
          <div className="config-modal-footer-left">
            {currentStep === 2 ? (
              <StorefrontServiceCardPreview
                tradeId={tradeId}
                subServiceType={subservice?.id}
                modalConfig={config}
              />
            ) : (
              <>
                <ShieldIcon sx={{ fontSize: 14 }} />
                <span>Step {currentStep} of 2</span>
              </>
            )}
          </div>

          <div className="config-modal-footer-actions">
            {currentStep > 1 ? (
              <button className="modal-btn-secondary" onClick={handleBack} type="button">
                Back
              </button>
            ) : (
              <button className="modal-btn-secondary" onClick={onClose} type="button">
                Cancel
              </button>
            )}

            {currentStep < 2 ? (
              <button
                className="modal-btn-primary"
                onClick={handleNext}
                disabled={!stepValid}
                type="button"
              >
                Continue
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </button>
            ) : (
              <button
                className={`modal-btn-primary${externalSaving ? ' saving' : ''}`}
                onClick={handleSave}
                disabled={!allValid || externalSaving}
                type="button"
              >
                {externalSaving ? (
                  <>
                    <NkySpinner size="sm" onDark />
                    Saving…
                  </>
                ) : (
                  <>
                    <CheckIcon sx={{ fontSize: 16 }} />
                    {existingSubService ? 'Save Changes' : 'Enable Service'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const ServiceDashboard = () => {
  // ─── Loading / error state ──────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // ─── Business & user data ──────────────────────────────────────────────
  const [businessData, setBusinessData] = useState(null);
  const [userData, setUserData] = useState(null);

  // ─── Trade state derived from API ───────────────────────────────────────
  // Shape: { [tradeId]: { ServiceId, Quoting, IsSchedulable, subServices: { [subType]: {...} } } }
  // A trade is present here iff a Service exists for it on the backend (even with 0 sub-services).
  const [trades, setTrades] = useState({});

  const [expandedTrades, setExpandedTrades] = useState(new Set());
  const [expandedGroupsByTrade, setExpandedGroupsByTrade] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // ── Modal state ─────────────────────────────────────────────────────────
  const [configModal, setConfigModal] = useState({
    open: false,
    tradeId: null,
    subservice: null,
    existingSubService: null,
  });

  // mode: 'enroll' (POST a new trade) or 'edit' (PATCH an enrolled trade's
  // Quoting/FinancingAvailable config). initialConfig seeds the modal when editing.
  const [enrollModal, setEnrollModal] = useState({
    open: false,
    trade: null,
    mode: 'enroll',
    initialConfig: null,
  });

  const [businessDefinedModal, setBusinessDefinedModal] = useState({
    open: false,
    tradeId: null,
    tradeName: '',
    existingSubService: null,
  });

  // ─── Fetch business data + services on mount ───────────────────────────
  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const userResponse = await accountService.getAccount();
        if (isCancelled) return;
        setUserData(userResponse);

        if (!userResponse.BusinessId) {
          setError('No business associated with your account.');
          setLoading(false);
          return;
        }

        const businessResponse =
          await businessService.getBusinessForOwnerDashboard(
            userResponse.BusinessId,
          );
        if (isCancelled) return;

        setBusinessData(businessResponse);

        const builtTrades = buildTradesFromApiServices(businessResponse.Services);
        setTrades(builtTrades);
        setExpandedTrades(new Set(Object.keys(builtTrades)));
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to load service dashboard:', err);
          setError('Failed to load your business data. Please try again.');
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      isCancelled = true;
    };
  }, []);

  // Auto-open accordion groups that contain at least one enabled sub-service
  useEffect(() => {
    setExpandedGroupsByTrade((prev) => {
      const next = { ...prev };
      ALL_TRADES.forEach((trade) => {
        const tradeState = trades[trade.id];
        const subMap = tradeState?.subServices || {};
        const s = new Set(prev[trade.id] ? [...prev[trade.id]] : []);
        (trade.groups || []).forEach((g) => {
          const hasEnabled = (g.subservices || []).some((sub) => sub.id in subMap);
          if (hasEnabled) s.add(g.id);
          else s.delete(g.id);
        });
        next[trade.id] = s;
      });
      return next;
    });
  }, [trades]);

  // ─── Derived state ──────────────────────────────────────────────────────

  // A trade is "enrolled" anytime a Service record exists for it on the backend,
  // regardless of whether any sub-services have been added yet.
  const enrolledTradeIds = useMemo(() => new Set(Object.keys(trades)), [trades]);

  // "Active" trades are the ones we render in the top section — same as enrolled.
  const activeTradeIds = enrolledTradeIds;

  const activeTrades = useMemo(
    () => ALL_TRADES.filter((t) => activeTradeIds.has(t.id)),
    [activeTradeIds],
  );

  const availableTrades = useMemo(
    () => ALL_TRADES.filter((t) => !activeTradeIds.has(t.id)),
    [activeTradeIds],
  );

  // ─── Stats ──────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    let totalEnabled = 0;
    let totalAvailable = 0;
    activeTrades.forEach((trade) => {
      const tradeState = trades[trade.id];
      totalEnabled += Object.keys(tradeState?.subServices || {}).length;
      if (!isBusinessDefinedTrade(trade)) {
        totalAvailable += getTradeSubservicesFlat(trade).length;
      }
    });
    return { totalEnabled, totalAvailable, tradeCount: enrolledTradeIds.size };
  }, [activeTrades, trades, enrolledTradeIds]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const toggleTradeExpand = useCallback((tradeId) => {
    setExpandedTrades((prev) => {
      const next = new Set(prev);
      if (next.has(tradeId)) next.delete(tradeId);
      else next.add(tradeId);
      return next;
    });
  }, []);

  /**
   * "Enroll" click — opens the settings modal in 'enroll' mode so the user
   * can pick Quoting + FinancingAvailable config before we POST.
   */
  const enrollInTrade = useCallback((tradeId) => {
    const trade = ALL_TRADES.find((t) => t.id === tradeId);
    if (!trade) return;
    setEnrollModal({ open: true, trade, mode: 'enroll', initialConfig: null });
  }, []);

  /**
   * "Edit" click on an already-enrolled trade — opens the same modal in
   * 'edit' mode, pre-filled with the trade's current Quoting/FinancingAvailable,
   * so the user can change quotes, quoting method, or financing.
   */
  const openEditTradeModal = useCallback(
    (tradeId) => {
      const trade = ALL_TRADES.find((t) => t.id === tradeId);
      const tradeState = trades[tradeId];
      if (!trade || !tradeState) return;
      setEnrollModal({
        open: true,
        trade,
        mode: 'edit',
        initialConfig: {
          FreeQuotes: tradeState.Quoting.FreeQuotes,
          QuotingMethod: tradeState.Quoting.QuotingMethod,
          FinancingAvailable: tradeState.FinancingAvailable,
        },
      });
    },
    [trades],
  );

  /**
   * Modal confirm — behavior depends on enrollModal.mode:
   *   - 'enroll' → POST /service-api/ creates a Service shell with the
   *     user-selected Quoting + FinancingAvailable config. No sub-services
   *     are created here; the user must add at least one for the listing
   *     to be visible to consumers.
   *   - 'edit'   → PATCH /service-api/{ServiceId} with just the
   *     service-level config (Quoting + FinancingAvailable); sub-services
   *     are left untouched.
   */
  const handleEnrollConfirm = useCallback(
    async (tradeId, config) => {
      if (!businessData?.BusinessId) return;
      const isEdit = enrollModal.mode === 'edit';
      const quoting = { FreeQuotes: config.FreeQuotes, QuotingMethod: config.QuotingMethod };

      setSaving(true);
      setError(null);
      try {
        if (isEdit) {
          const tradeState = trades[tradeId];
          if (!tradeState?.ServiceId) {
            setError('Service not found. Please refresh and try again.');
            return;
          }

          const updated = await servicesService.patchService(
            businessData.BusinessId,
            tradeState.ServiceId,
            { Quoting: quoting, FinancingAvailable: config.FinancingAvailable },
          );

          setTrades((prev) => ({
            ...prev,
            [tradeId]: rebuildTradeFromPatchResponse(prev[tradeId], updated),
          }));
        } else {
          const payload = {
            BusinessId: businessData.BusinessId,
            ServiceType: tradeId,
            Quoting: quoting,
            FinancingAvailable: config.FinancingAvailable,
            IsSchedulable: false,
          };

          const created = await servicesService.createService(payload);

          setTrades((prev) => ({
            ...prev,
            [tradeId]: {
              ServiceId: created.ServiceId,
              Quoting: created.Quoting,
              IsSchedulable: created.IsSchedulable || false,
              FinancingAvailable: created.FinancingAvailable || false,
              subServices: {},
            },
          }));
          setExpandedTrades((prev) => new Set([...prev, tradeId]));
        }

        setEnrollModal({ open: false, trade: null, mode: 'enroll', initialConfig: null });
      } catch (err) {
        console.error(isEdit ? 'Failed to update trade settings:' : 'Failed to enroll in trade:', err);
        setError(
          err.message ||
            (isEdit
              ? 'Failed to update trade settings. Please try again.'
              : 'Failed to enroll in trade. Please try again.'),
        );
      } finally {
        setSaving(false);
      }
    },
    [businessData, trades, enrollModal.mode],
  );

  const handleEnrollClose = useCallback(() => {
    setEnrollModal({ open: false, trade: null, mode: 'enroll', initialConfig: null });
  }, []);

  /**
   * Unenroll from a trade — DELETE /service-api/{ServiceId}.
   * Removes the entire service and all sub-services in one call.
   */
  const unenrollFromTrade = useCallback(
    async (tradeId) => {
      if (!businessData?.BusinessId) return;
      const tradeState = trades[tradeId];
      if (!tradeState?.ServiceId) return;

      const subCount = Object.keys(tradeState.subServices).length;
      if (subCount > 0) {
        const confirmMsg = `This will remove all ${subCount} enabled service(s) for this trade. Continue?`;
        if (!window.confirm(confirmMsg)) return;
      }

      setSaving(true);
      setError(null);
      try {
        await servicesService.deleteService(
          businessData.BusinessId,
          tradeState.ServiceId,
        );

        setTrades((prev) => {
          const next = { ...prev };
          delete next[tradeId];
          return next;
        });
        setExpandedTrades((prev) => {
          const next = new Set(prev);
          next.delete(tradeId);
          return next;
        });
      } catch (err) {
        console.error('Failed to unenroll from trade:', err);
        setError(err.message || 'Failed to remove services. Please try again.');
      } finally {
        setSaving(false);
      }
    },
    [businessData, trades],
  );

  /**
   * Toggle sub-service:
   *   - Enabling  → open the configuration modal (PATCH happens on save).
   *   - Disabling → DELETE /service-api/{ServiceId}?subServiceType=...
   *                 If this is the last sub-service, the API auto-deletes the whole service.
   */
  const handleSubserviceToggle = useCallback(
    async (tradeId, subservice) => {
      const tradeState = trades[tradeId];
      if (!tradeState) return;

      const existing = tradeState.subServices[subservice.id];

      if (existing) {
        // Disable
        if (!businessData?.BusinessId) return;
        setSaving(true);
        setError(null);
        try {
          await servicesService.deleteSubService(
            businessData.BusinessId,
            tradeState.ServiceId,
            subservice.id,
          );

          // DELETE with subServiceType only removes the sub-service — the parent
          // Service stays, so the user remains enrolled in the trade.
          setTrades((prev) => {
            const next = { ...prev };
            const t = next[tradeId];
            if (!t) return prev;
            const newSubs = { ...t.subServices };
            delete newSubs[subservice.id];
            next[tradeId] = { ...t, subServices: newSubs };
            return next;
          });
        } catch (err) {
          console.error('Failed to disable service:', err);
          setError(err.message || 'Failed to disable service. Please try again.');
        } finally {
          setSaving(false);
        }
      } else {
        // Enable → open modal
        setConfigModal({
          open: true,
          tradeId,
          subservice,
          existingSubService: null,
        });
      }
    },
    [trades, businessData],
  );

  /**
   * Open the modal to edit an existing sub-service.
   */
  const handleEditConfig = useCallback(
    (tradeId, subservice) => {
      const tradeState = trades[tradeId];
      const existing = tradeState?.subServices[subservice.id] || null;
      setConfigModal({
        open: true,
        tradeId,
        subservice,
        existingSubService: existing,
      });
    },
    [trades],
  );

  /**
   * Modal save → PATCH /service-api/{ServiceId} with a single SubService payload.
   * This works for both creating a new sub-service and editing an existing one
   * (the API matches by SubServiceType and fully replaces the sub-service).
   */
  const handleModalSave = useCallback(
    async (tradeId, subserviceId, modalConfig) => {
      if (!businessData?.BusinessId) return;
      const tradeState = trades[tradeId];
      if (!tradeState?.ServiceId) {
        setError('Service not found. Please refresh and try again.');
        return;
      }

      setSaving(true);
      setError(null);
      try {
        const body = buildSubServicePayload(subserviceId, modalConfig);
        const updated = await servicesService.patchService(
          businessData.BusinessId,
          tradeState.ServiceId,
          body,
        );

        // Rebuild this trade's state from the API response (single source of truth)
        setTrades((prev) => ({
          ...prev,
          [tradeId]: rebuildTradeFromPatchResponse(prev[tradeId], updated),
        }));

        setConfigModal({
          open: false,
          tradeId: null,
          subservice: null,
          existingSubService: null,
        });
      } catch (err) {
        console.error('Failed to save service config:', err);
        setError(err.message || 'Failed to save service. Please try again.');
      } finally {
        setSaving(false);
      }
    },
    [businessData, trades],
  );

  const handleModalClose = useCallback(() => {
    setConfigModal({
      open: false,
      tradeId: null,
      subservice: null,
      existingSubService: null,
    });
  }, []);

  const handleBusinessDefinedModalClose = useCallback(() => {
    setBusinessDefinedModal({
      open: false,
      tradeId: null,
      tradeName: '',
      existingSubService: null,
    });
  }, []);

  const handleBusinessDefinedSave = useCallback(
    async (body) => {
      const { tradeId } = businessDefinedModal;
      if (!tradeId || !businessData?.BusinessId) return;
      const tradeState = trades[tradeId];
      if (!tradeState?.ServiceId) {
        setError('Service not found. Please refresh and try again.');
        return;
      }

      setSaving(true);
      setError(null);
      try {
        const updated = await servicesService.patchService(
          businessData.BusinessId,
          tradeState.ServiceId,
          body,
        );

        setTrades((prev) => ({
          ...prev,
          [tradeId]: rebuildTradeFromPatchResponse(prev[tradeId], updated),
        }));

        handleBusinessDefinedModalClose();
      } catch (err) {
        console.error('Failed to save custom service:', err);
        setError(err.message || 'Failed to save service. Please try again.');
      } finally {
        setSaving(false);
      }
    },
    [businessDefinedModal, businessData, trades, handleBusinessDefinedModalClose],
  );

  const handleBusinessDefinedDelete = useCallback(
    async (tradeId, subServiceType) => {
      if (!businessData?.BusinessId) return;
      const tradeState = trades[tradeId];
      if (!tradeState?.ServiceId) return;

      if (!window.confirm('Remove this service from your listing?')) return;

      setSaving(true);
      setError(null);
      try {
        await servicesService.deleteSubService(
          businessData.BusinessId,
          tradeState.ServiceId,
          subServiceType,
        );

        setTrades((prev) => {
          const next = { ...prev };
          const t = next[tradeId];
          if (!t) return prev;
          const newSubs = { ...t.subServices };
          delete newSubs[subServiceType];
          next[tradeId] = { ...t, subServices: newSubs };
          return next;
        });
      } catch (err) {
        console.error('Failed to delete custom service:', err);
        setError(err.message || 'Failed to remove service. Please try again.');
      } finally {
        setSaving(false);
      }
    },
    [businessData, trades],
  );

  // ─── Filter available trades by search ──────────────────────────────────
  const filteredAvailableTrades = useMemo(() => {
    if (!searchQuery.trim()) return availableTrades;
    const q = searchQuery.toLowerCase();
    return availableTrades.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        getTradeSubservicesFlat(t).some((s) => s.label.toLowerCase().includes(q)) ||
        (t.groups || []).some(
          (g) =>
            g.name.toLowerCase().includes(q) ||
            g.description.toLowerCase().includes(q),
        ),
    );
  }, [availableTrades, searchQuery]);

  // ─── Config summary helper ────────────────────────────────────────────────
  const getConfigSummary = (subService) => {
    if (!subService) return '';
    const parts = [];
    const pricing = subService.Pricing || {};

    switch (pricing.PriceType) {
      case 'Range': {
        const rangeLabel = formatCurrencyRange(pricing.Min, pricing.Max, {
          separator: '–',
        });
        if (rangeLabel) parts.push(rangeLabel);
        break;
      }
      case 'Fixed':
        if (pricing.Price != null) {
          const fixedLabel = formatCurrency(pricing.Price);
          if (fixedLabel) parts.push(`${fixedLabel} flat`);
        }
        break;
      case 'NA':
        parts.push('Pricing hidden');
        break;
      default:
        break;
    }

    const tagline =
      typeof subService.Tagline === 'string' && subService.Tagline.trim()
        ? subService.Tagline.trim()
        : '';
    if (tagline) parts.push(tagline);

    return parts.join(' · ');
  };

  // ─── Render: Trade card (active — enrolled or browsing) ─────────────────
  const renderActiveTrade = (trade) => {
    const TradeIcon = trade.icon;
    const isExpanded = expandedTrades.has(trade.id);
    const tradeState = trades[trade.id];
    const subMap = tradeState?.subServices || {};
    const expandedGroupSet = expandedGroupsByTrade[trade.id] || new Set();
    const enabledCount = Object.keys(subMap).length;
    const totalCount = getTradeSubservicesFlat(trade).length;
    const businessDefined = isBusinessDefinedTrade(trade);
    const customSubs = Object.values(subMap);

    return (
      <div key={trade.id} className={`trade-card enrolled trade-${trade.id}`}>
        <div
          className="trade-header"
          onClick={() => toggleTradeExpand(trade.id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleTradeExpand(trade.id);
            }
          }}
          aria-expanded={isExpanded}
        >
          <div className={`trade-icon-wrap trade-color-${trade.id}`}>
            <TradeIcon sx={{ fontSize: 22 }} />
          </div>
          <div className="trade-info">
            <Typography variant="subtitle1" className="trade-name">
              {trade.name}
            </Typography>
            <span className="trade-meta">
              {businessDefined
                ? `${enabledCount} service${enabledCount === 1 ? '' : 's'} defined`
                : `${enabledCount} of ${totalCount} services enabled`}
            </span>
          </div>
          <div className="trade-header-actions">
            <span className="enrollment-badge active">Enrolled</span>
            <span className="expand-icon">
              {isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: 22 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 22 }} />
              )}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className="trade-body">
            <div className="subservice-controls">
              <button
                type="button"
                className="bulk-toggle-btn"
                onClick={() => openEditTradeModal(trade.id)}
                disabled={saving}
              >
                <SettingsIcon sx={{ fontSize: 14 }} />
                Edit quotes &amp; financing
              </button>
              <button
                type="button"
                className="unenroll-btn"
                onClick={() => unenrollFromTrade(trade.id)}
                disabled={saving}
              >
                {saving ? 'Removing…' : 'Leave this trade'}
              </button>
            </div>

            <div className="subservice-list">
              {businessDefined ? (
                <>
                  <div className="subservice-controls" style={{ marginBottom: 12 }}>
                    <button
                      type="button"
                      className="enroll-btn"
                      onClick={() =>
                        setBusinessDefinedModal({
                          open: true,
                          tradeId: trade.id,
                          tradeName: trade.name,
                          existingSubService: null,
                        })
                      }
                      disabled={saving}
                    >
                      <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                      Add service
                    </button>
                  </div>
                  {customSubs.length === 0 ? (
                    <div className="empty-state">
                      Add at least one service so customers can find and contact you.
                    </div>
                  ) : (
                    customSubs.map((sub) => {
                      const configSummary = getConfigSummary(sub);
                      return (
                        <div
                          key={sub.SubServiceType}
                          className="subservice-item enabled"
                        >
                          <div className="subservice-row">
                            <div className={`subservice-icon trade-color-${trade.id}`}>
                              <HomeRepairServiceIcon sx={{ fontSize: 16 }} />
                            </div>
                            <div className="subservice-info">
                              <span className="subservice-label">
                                {resolveSubserviceTitle(trade.id, sub)}
                              </span>
                              {sub.Description && (
                                <span className="subservice-desc">{sub.Description}</span>
                              )}
                              {configSummary && (
                                <span className="config-summary">{configSummary}</span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="config-toggle-btn"
                              onClick={() =>
                                setBusinessDefinedModal({
                                  open: true,
                                  tradeId: trade.id,
                                  tradeName: trade.name,
                                  existingSubService: sub,
                                })
                              }
                              title="Edit service"
                              disabled={saving}
                            >
                              <SettingsIcon sx={{ fontSize: 16 }} />
                            </button>
                            <button
                              type="button"
                              className="config-toggle-btn"
                              onClick={() =>
                                handleBusinessDefinedDelete(trade.id, sub.SubServiceType)
                              }
                              title="Remove service"
                              disabled={saving}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              ) : (
              (trade.groups || []).map((group) => {
                const GroupIcon = group.icon;
                const groupEnabledCount = (group.subservices || []).filter(
                  (sub) => sub.id in subMap,
                ).length;
                const accordionExpanded = expandedGroupSet.has(group.id);

                return (
                  <Accordion
                    key={group.id}
                    className="service-group-accordion"
                    expanded={accordionExpanded}
                    onChange={(event, nextExpanded) => {
                      event.stopPropagation();
                      setExpandedGroupsByTrade((prev) => {
                        const s = new Set(
                          prev[trade.id] ? [...prev[trade.id]] : [],
                        );
                        if (nextExpanded) s.add(group.id);
                        else s.delete(group.id);
                        return { ...prev, [trade.id]: s };
                      });
                    }}
                    disableGutters
                    elevation={0}
                    sx={{
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      background: 'transparent',
                    }}
                  >
                    <AccordionSummary
                      className="subservice-group-accordion-summary"
                      expandIcon={
                        <ExpandMoreIcon sx={{ fontSize: 22, color: '#6b7280' }} />
                      }
                      sx={{
                        minHeight: 0,
                        px: 0,
                        py: 0,
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0',
                          alignItems: 'flex-start',
                        },
                      }}
                    >
                      <div className="subservice-group-header">
                        <div
                          className="subservice-group-icon"
                          style={{
                            backgroundColor: `${group.color}22`,
                            color: group.color,
                          }}
                        >
                          <GroupIcon sx={{ fontSize: 18 }} />
                        </div>
                        <div className="subservice-group-text">
                          <div className="subservice-group-name-row">
                            <span className="subservice-group-name">
                              {group.name}
                            </span>
                            {groupEnabledCount > 0 && (
                              <span className="subservice-group-badge">
                                {groupEnabledCount} enabled
                              </span>
                            )}
                          </div>
                          <div className="subservice-group-desc">
                            {group.description}
                          </div>
                        </div>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0, pt: 0 }}>
                      {(group.subservices || []).map((sub) => {
                        const SubIcon = sub.icon;
                        const isEnabled = sub.id in subMap;
                        const subData = subMap[sub.id];
                        const configSummary = isEnabled ? getConfigSummary(subData) : '';

                        return (
                          <div
                            key={sub.id}
                            className={`subservice-item${isEnabled ? ' enabled' : ''}${sub.urgent ? ' urgent' : ''}`}
                          >
                            <div
                              className="subservice-row"
                              onClick={() => handleSubserviceToggle(trade.id, sub)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleSubserviceToggle(trade.id, sub);
                                }
                              }}
                            >
                              <div className="subservice-toggle">
                                {isEnabled ? (
                                  <ToggleOnIcon sx={{ fontSize: 28 }} className="toggle-on" />
                                ) : (
                                  <ToggleOffIcon sx={{ fontSize: 28 }} className="toggle-off" />
                                )}
                              </div>
                              <div className={`subservice-icon trade-color-${trade.id}`}>
                                <SubIcon sx={{ fontSize: 16 }} />
                              </div>
                              <div className="subservice-info">
                                <span className="subservice-label">
                                  {sub.label}
                                  {sub.urgent && <span className="urgent-tag">24/7</span>}
                                </span>
                                <span className="subservice-desc">
                                  {isEnabled && subData?.Description
                                    ? subData.Description
                                    : sub.description}
                                </span>
                                {isEnabled && configSummary && (
                                  <span className="config-summary">{configSummary}</span>
                                )}
                              </div>
                              {isEnabled && (
                                <button
                                  type="button"
                                  className="config-toggle-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditConfig(trade.id, sub);
                                  }}
                                  title="Edit service configuration"
                                >
                                  <SettingsIcon sx={{ fontSize: 16 }} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Trade card (available / not enrolled) ────────────────────────
  const renderAvailableTrade = (trade) => {
    const TradeIcon = trade.icon;
    return (
      <div key={trade.id} className={`trade-card available trade-${trade.id}`}>
        <div className="trade-header available-header">
          <div className={`trade-icon-wrap trade-color-${trade.id}`}>
            <TradeIcon sx={{ fontSize: 22 }} />
          </div>
          <div className="trade-info">
            <Typography variant="subtitle1" className="trade-name">
              {trade.name}
            </Typography>
            <span className="trade-meta">{trade.description}</span>
          </div>
          <button
            type="button"
            className="enroll-btn"
            onClick={() => enrollInTrade(trade.id)}
            disabled={saving}
          >
            <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
            {saving ? 'Enrolling…' : 'Enroll'}
          </button>
        </div>
        <div className="available-preview">
          {isBusinessDefinedTrade(trade) ? (
            <span className="preview-chip">Define your own services</span>
          ) : (
            (() => {
              const subs = getTradeSubservicesFlat(trade);
              return (
                <>
                  {subs.slice(0, 4).map((sub) => (
                    <span key={sub.id} className="preview-chip">
                      {sub.label}
                    </span>
                  ))}
                  {subs.length > 4 && (
                    <span className="preview-chip more">+{subs.length - 4} more</span>
                  )}
                </>
              );
            })()
          )}
        </div>
      </div>
    );
  };

  const businessName = businessData?.BusinessName || 'Your Business';

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <AppProvider branding={{ title: businessName }} theme={appTheme}>
      <DashboardContainer>
        <SidebarContainer>
          <DashboardNavigationBar />
        </SidebarContainer>

        <ContentWrapper>
          {businessData && !businessData.PartnershipAgreementAcceptedAt && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
              Sign the{' '}
              <Link
                to="/dashboard/partnership-agreement"
                style={{ color: '#0d4efe', fontWeight: 700 }}
              >
                Business Partnership Agreement
              </Link>{' '}
              before your storefront can be published.
            </Alert>
          )}
          {businessData && !businessData.BusinessPublished && <StorefrontOfflineAlert />}
          <ServiceDashboardWrapper>
            <div className="dashboard-page">
              {/* ── Trust banner ─────────────────────────────────────── */}
              <div className="trust-banner">
                <div className="trust-banner-inner">
                  <div className="trust-item">
                    <LocationOnIcon sx={{ fontSize: 16 }} />
                    <span>NKY Connect — Northern Kentucky</span>
                  </div>
                  <div className="trust-divider" />
                  <div className="trust-item">
                    <VerifiedIcon sx={{ fontSize: 16 }} />
                    <span>Business Service Dashboard</span>
                  </div>
                  <div className="trust-divider" />
                  <div className="trust-item">
                    <StorefrontIcon sx={{ fontSize: 16 }} />
                    <span>{businessName}</span>
                  </div>
                </div>
              </div>

              <div className="content-area">
                {loading && (
                  <div className="loading-state">
                    <NkySpinner label="Loading your services" />
                    <Typography variant="body1">Loading your services…</Typography>
                  </div>
                )}

                {error && !loading && (
                  <div className="error-banner">
                    <AlertCircleIcon sx={{ fontSize: 18 }} />
                    <span>{error}</span>
                    <button
                      type="button"
                      className="error-dismiss"
                      onClick={() => setError(null)}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </button>
                  </div>
                )}

                {!loading && (
                  <>
                    <div className="page-header">
                      <div className="page-title-row">
                        <HomeRepairServiceIcon className="page-icon" sx={{ fontSize: 28 }} />
                        <div>
                          <Typography variant="h5" className="page-title">
                            Welcome back, {userData?.FirstName?.trim() || 'there'}!
                          </Typography>
                          <Typography variant="body2" className="page-subtitle">
                            Manage your business and choose which services you want to receive customer requests for.
                          </Typography>
                        </div>
                      </div>
                    </div>

                    <div className="stats-strip">
                      <div className="stat-card">
                        <span className="stat-number">{stats.tradeCount}</span>
                        <span className="stat-label">Trades enrolled</span>
                      </div>
                      <div className="stat-divider" />
                      <div className="stat-card">
                        <span className="stat-number">{stats.totalEnabled}</span>
                        <span className="stat-label">Services active</span>
                      </div>
                      <div className="stat-divider" />
                      <div className="stat-card">
                        <span className="stat-number">
                          {stats.totalAvailable - stats.totalEnabled}
                        </span>
                        <span className="stat-label">Available to enable</span>
                      </div>
                    </div>

                    {activeTrades.length > 0 && (
                      <section className="section enrolled-section">
                        <div className="section-label">
                          <CheckCircleIcon sx={{ fontSize: 18 }} />
                          <Typography variant="overline">Your Trades</Typography>
                        </div>
                        <div className="trade-list">
                          {activeTrades.map(renderActiveTrade)}
                        </div>
                      </section>
                    )}

                    {availableTrades.length > 0 && (
                      <section className="section available-section">
                        <div className="section-label">
                          <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                          <Typography variant="overline">Available Trades</Typography>
                        </div>

                        <div className="info-banner">
                          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                          <span>
                            Enroll in a trade to start receiving service requests. You can enable or
                            disable individual services at any time.
                          </span>
                        </div>

                        {availableTrades.length > 3 && (
                          <div className="search-bar">
                            <SearchIcon sx={{ fontSize: 18 }} />
                            <input
                              type="text"
                              placeholder="Search trades or services..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                            />
                          </div>
                        )}

                        <div className="trade-list">
                          {filteredAvailableTrades.map(renderAvailableTrade)}
                          {filteredAvailableTrades.length === 0 && searchQuery && (
                            <div className="empty-state">
                              No trades match &ldquo;{searchQuery}&rdquo;
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                  </>
                )}
              </div>
            </div>

            <ServiceConfigModal
              open={configModal.open}
              onClose={handleModalClose}
              onSave={handleModalSave}
              subservice={configModal.subservice}
              tradeId={configModal.tradeId}
              existingSubService={configModal.existingSubService}
              saving={saving}
            />

            <TradeSettingsModal
              open={enrollModal.open}
              onClose={handleEnrollClose}
              onConfirm={handleEnrollConfirm}
              trade={enrollModal.trade}
              saving={saving}
              mode={enrollModal.mode}
              initialConfig={enrollModal.initialConfig}
            />

            <BusinessDefinedServiceModal
              open={businessDefinedModal.open}
              onClose={handleBusinessDefinedModalClose}
              onSave={handleBusinessDefinedSave}
              tradeName={businessDefinedModal.tradeName}
              existingSubService={businessDefinedModal.existingSubService}
              saving={saving}
            />
          </ServiceDashboardWrapper>
        </ContentWrapper>
      </DashboardContainer>
    </AppProvider>
  );
};

export { ServiceDashboard };
export default ServiceDashboard;